import React from 'react';
import { Select, MenuItem, Typography, CircularProgress, Box, Button } from '@mui/material';
import { RefreshCw } from 'lucide-react';
import { extractName, standardInputSx } from '../utils/hubsHelpers';

const ApiSelect = ({
  value,
  onChange,
  dropdown,
  placeholder = 'Any',
  disabled = false,
  emptyOptionLabel = 'Any',
  sx = standardInputSx
}) => {
  const { options, loading, error, loaded, loadOptions, retry } = dropdown;
  const stringValue = extractName(value);

  return (
    <Select
      size="small"
      fullWidth
      value={stringValue}
      onChange={onChange}
      onOpen={loadOptions}
      disabled={disabled}
      displayEmpty
      sx={sx}
      renderValue={(selected) => {
        const selStr = extractName(selected);
        if (!selStr) {
          return <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>{placeholder}</Typography>;
        }
        return selStr;
      }}
    >
      {/* Empty / Reset option */}
      {emptyOptionLabel && (
        <MenuItem value="">
          <em>{emptyOptionLabel}</em>
        </MenuItem>
      )}

      {/* Preserve current value even before opening dropdown */}
      {stringValue && !options.some((opt) => extractName(opt) === stringValue) && (
        <MenuItem key={stringValue} value={stringValue} sx={{ display: 'none' }}>
          {stringValue}
        </MenuItem>
      )}

      {/* Loading state */}
      {loading && (
        <MenuItem disabled sx={{ py: 1.5, display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
          <CircularProgress size={16} sx={{ color: '#FFBD65' }} />
          <Typography sx={{ fontSize: 12, color: '#64748B' }}>Loading options...</Typography>
        </MenuItem>
      )}

      {/* Error state with retry */}
      {error && !loading && (
        <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'flex-start' }}>
          <Typography sx={{ fontSize: 12, color: '#DC2626' }}>{error}</Typography>
          <Button
            size="small"
            variant="text"
            onClick={(e) => {
              e.stopPropagation();
              retry();
            }}
            sx={{
              p: 0,
              minWidth: 'auto',
              color: '#803927',
              fontSize: 11.5,
              fontWeight: 600,
              textTransform: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' }
            }}
          >
            <RefreshCw size={12} /> Retry
          </Button>
        </Box>
      )}

      {/* Empty response state */}
      {!loading && !error && loaded && options.length === 0 && (
        <MenuItem disabled sx={{ py: 1.5, fontSize: 12, color: '#94A3B8', justifyContent: 'center' }}>
          No options available
        </MenuItem>
      )}

      {/* Populated options */}
      {!loading && !error && options.map((opt) => {
        const optVal = extractName(opt);
        const optKey = typeof opt === 'object' && opt.id ? opt.id : optVal;
        return (
          <MenuItem key={optKey} value={optVal}>
            {optVal}
          </MenuItem>
        );
      })}
    </Select>
  );
};

export default React.memo(ApiSelect);
